import { getAuth, signOut } from "firebase/auth";
import { toast } from "@/components/ui/sonner";
import { getOwnUserProfile, type UserProfile } from "@/services/firebaseService";
import { hasProAccessRole } from "@/lib/proAccessRole";
import { emitAnalyticsEvent } from "@/lib/analytics/emitEvent";
import { ANALYTICS_EVENTS } from "@/lib/analytics/eventNames";

/**
 * KB-3133 — the one place the pro sign-in allowlist is ENFORCED.
 *
 * KB-3064 installed the allowlist on the Google button and nowhere else, so
 * the same harm stayed reachable with a password. The fix is not a second copy
 * of that check: `hasProAccessRole` was already shared, and the two doors still
 * drifted, because what actually differed was everything AROUND the predicate —
 * which read runs, whether a failed read signs the session out, and which of
 * two sentences the person is shown. That is the part this module makes single.
 *
 * `proAccessRole.ts` answers "may this role sign in" and is pure. This module
 * answers "may THIS CALLER sign in, right now" — it performs the read, disposes
 * of the session, and renders the verdict. Callers decide navigation only.
 *
 * ## The two rejections are different facts and must stay different sentences
 *
 * A role that is not on the allowlist is a statement ABOUT AUTHORIZATION. A
 * read that failed is a statement about our own instrument. Collapsing them is
 * how KB-2306 signed out every legitimate camp user, and it is the
 * calm-wrong-answer shape the dispatch SOP §6a-bis names: never render an
 * absence you did not verify. `getOwnUserProfile` throws rather than returning
 * null on a denial (deliberately, KB-2306), which is what keeps the two
 * distinguishable here.
 *
 * ## Both outcomes sign the caller out, for different reasons
 *
 * `denied` — the check ran and refused them.
 * `unverifiable` — the check could not run, so the session is unverified by
 * construction. Leaving it would mean a session that never passed the gate,
 * which is the whole defect.
 */

/**
 * Shown to a caller whose role is not on the allowlist.
 *
 * The wording asserts nothing about a pending access request, because nothing
 * on this path can read one — `accountRequests` is denied to every non-admin
 * (KB-3140 owns that). It gives someone who did request access the one action
 * that helps them without claiming we looked.
 */
export const PRO_ACCESS_DENIED_MESSAGE =
  "Your account is not authorized for KidsBooked Pro. If you have requested access, please contact support.";

/** Shown when the gate's own read failed. Says what is true: we could not tell. */
export const PRO_ACCESS_UNVERIFIABLE_MESSAGE =
  "We couldn't verify your access just now. Please try again in a moment.";

/**
 * Which door the caller came through. For the three INTERACTIVE doors it reaches
 * analytics and nothing else: the decision and the copy are identical for
 * `email`/`google`/`create-account`, which is what KB-3133 AC-3 requires — a
 * stranger must not be able to tell which door was locked more recently.
 *
 * `apex-sso` (KB-3228) is the exception, and the reason this stayed a union
 * rather than collapsing to a boolean: its rejection is SILENT (see
 * `SILENT_REJECTION_DOORS` below). The decision and the analytics are unchanged
 * for it — only the toast is suppressed. The union is deliberately exhaustive so
 * a new door is a compile error until someone decides how it rejects; never
 * widen a call site to `string`.
 */
export type ProAccessDoor = "email" | "google" | "create-account" | "apex-sso";

export type ProAccessVerdict =
  | { outcome: "allowed"; profile: UserProfile }
  | { outcome: "denied" }
  | { outcome: "unverifiable" };

/** Matches the Google path's rejection toast exactly — sticky, with a dismiss. */
const REJECTION_TOAST_OPTIONS = {
  duration: Infinity,
  action: { label: "✕", onClick: () => {} },
} as const;

/**
 * KB-3228 — the doors whose rejection is SILENT (no user-facing toast).
 *
 * Every interactive door is reached by someone who submitted a login form and is
 * waiting on a verdict; the sticky toast IS that verdict. The `apex-sso` door
 * fires automatically from a kidsbooked.com session cookie for someone who
 * clicked a link into pro and never asked to sign in here — so a rejection they
 * cannot act on is noise, and a sticky one announces that pro exists and refused
 * them. On this door the gate still RUNS, still signs them out, and still emits
 * both refusal-analytics arms below (the fact stays queryable server-side); only
 * the toast is withheld, and only on this door. `denied` and `unverifiable` stay
 * distinct outcomes, so the caller and the funnel can still tell them apart.
 *
 * A set (not a `door === "apex-sso"` check at each site) keeps the two rejection
 * arms reading identically and states the property in exactly one place.
 */
const SILENT_REJECTION_DOORS: ReadonlySet<ProAccessDoor> = new Set(["apex-sso"]);

/**
 * ⚠️ EVERY CALLER MUST EMIT ITS ANALYTICS BEFORE CALLING THIS.
 *
 * Round-1 finding 1. `sendInternalUiAction` opens with
 * `if (!auth.currentUser) return;` (`lib/analytics/internalSink.ts:83`) because
 * the `recordUiAction` callable requires an authenticated caller. Signing out
 * clears `currentUser`, so an `emitAnalyticsEvent` placed after this call
 * reaches GA4 (`trackEvent` never reads auth) and reaches the KB-2611 internal
 * sink NEVER — silently, since the sink is fire-and-forget by design.
 *
 * What that costs: the internal sink is the only record of a refusal that is
 * queryable server-side, and it would hold ZERO of them, forever, silently.
 *
 * ⚠️ Round-2 O2 corrected the story this docblock used to tell. It claimed the
 * funnel would show `auth_login_submit` with no matching `auth_login_failure`.
 * It would not: both submit emissions (`LoginPage.tsx:50` and `:106`) fire
 * before any credential exists, so `auth.currentUser` is null there too and the
 * internal sink drops the submit as well. There is no submit denominator to
 * compare against — do not compute a rejection rate from this sink.
 * `callSiteDiscipline.test.ts` asserts only call-site SHAPE, so it passes
 * happily either way.
 *
 * ⚠️ Round-2 O5, recorded because the guarantee is thinner than it looks.
 * `sendInternalUiAction` checks `auth.currentUser` synchronously, but
 * `@firebase/functions` then fetches the ID token asynchronously
 * (`makeAuthHeaders` → `getContext` → `await getAuthToken()`), and that path
 * reads `currentUser` a SECOND time — after control has returned here and this
 * function has awaited the sign-out. The reviewer traced it and could not make
 * it fire (the token path is microtasks on resolved promises, while
 * `AuthImpl.signOut()` clears IndexedDB persistence, so the token wins the
 * race), and neither could I. But the ordering is undocumented SDK behaviour: a
 * single added `await` ahead of `getAuthToken` upstream would revert this fix
 * silently. If refusals ever stop appearing in the sink, start here.
 */
async function signOutQuietly(reason: string): Promise<void> {
  try {
    await signOut(getAuth());
  } catch (signOutError) {
    // Reported, never rethrown: a failed sign-out must not turn a refusal into
    // the caller's generic "login failed" catch, which would replace the
    // rejection sentence with raw Firebase jargon and record the wrong reason
    // in the funnel.
    console.error(`Failed to sign out after ${reason}:`, signOutError);
  }
}

/**
 * ⛔ THIS MODULE HOLDS NO STATE, AND THAT IS A DECISION — KB-3133 round 3.
 *
 * A previous revision memoised "the uid this gate last admitted in this page
 * session" so the late-settle reconciler could skip re-gating a credential
 * belonging to an account admitted seconds earlier. It was deleted rather than
 * repaired, by dispatcher ruling, and it must not come back without one.
 *
 * The memo was written on `allowed` and cleared only on `denied` for the same
 * uid. Nothing cleared it on SIGN-OUT — and the in-app Logout button
 * (`DashboardLayout.tsx:33`) is `signOut().then(() => navigate('/login'))`, an
 * SPA navigation with no page reload, so the memo outlived the session it
 * described. That let a REVOKED account back into pro with no read, no role
 * check and no sign-out: sign in, log out, get revoked, sign in again, let the
 * login time out, and the late settle short-circuits on the stale admission.
 * The clear-on-denied could never help, because it only runs when the gate
 * runs, and the whole point of the short-circuit was to not run it.
 *
 * The general shape, worth more than the instance: a cache of an authorization
 * decision is a second source of truth with its own lifetime, and every
 * lifetime bug in it is an authorization bug. There is no read cheap enough
 * here to be worth that.
 *
 * So this module is a pure function of (uid, door) plus one Firestore read.
 * Every caller that needs a verdict calls it and gets a fresh one.
 */

/**
 * Read the caller's own profile and decide whether they may use pro.
 *
 * ⚠️ TIMING. This read runs while the caller holds a session but before any
 * role of ours is known, so it must be one THEY can perform: an owner read of
 * `users/{uid}` by uid. Never an email query over the collection — the KB-2291
 * read rule denies that to every non-admin, and it could not be role-gated
 * anyway, since the role is what we are here to find out.
 *
 * ⚠️ ORDERING, for `create-account`. A genuinely-approved applicant arrives
 * holding an invitation and carries `role: 'none'` until
 * `persistInviteToAccount` grants them one. Calling this before that grant
 * refuses every legitimate invitee. See CreateAccountPage's `createUserAccount`.
 */
export async function enforceProAccessGate(
  uid: string,
  door: ProAccessDoor,
): Promise<ProAccessVerdict> {
  let profile: UserProfile | null;
  try {
    profile = await getOwnUserProfile(uid);
  } catch (gateReadError) {
    console.error("Allowlist gate read failed; signing out unverified session:", gateReadError);
    // ⚠️ BEFORE the sign-out, and that ORDER IS LOAD-BEARING — see the note on
    // `signOutQuietly` above. (Round-2 O3: this said "the note on `emitRefusal`
    // below". There is no `emitRefusal` in this module and the note is above,
    // so the pointer was wrong in both coordinates.)
    emitAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_FAILURE, {
      method: door,
      reason: "allowlist-read-failed",
    });
    await signOutQuietly("a failed allowlist read");
    // Silent on the apex-SSO door only — see SILENT_REJECTION_DOORS. The analytics
    // above and the sign-out have already run; suppressing the toast changes what
    // the user sees, never what the gate does.
    if (!SILENT_REJECTION_DOORS.has(door)) {
      toast.error(PRO_ACCESS_UNVERIFIABLE_MESSAGE, REJECTION_TOAST_OPTIONS);
    }
    return { outcome: "unverifiable" };
  }

  if (!hasProAccessRole(profile)) {
    // ⚠️ BEFORE the sign-out — see `signOutQuietly`. Round-1 finding 1.
    emitAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_FAILURE, {
      method: door,
      reason: "not-authorized",
    });
    await signOutQuietly("a rejected sign-in");
    // Silent on the apex-SSO door only — see SILENT_REJECTION_DOORS. Same shape as
    // the unverifiable arm above: the refusal still happens and is still counted;
    // only the sentence is withheld, and only for a caller who never asked to
    // sign in.
    if (!SILENT_REJECTION_DOORS.has(door)) {
      toast.error(PRO_ACCESS_DENIED_MESSAGE, REJECTION_TOAST_OPTIONS);
    }
    return { outcome: "denied" };
  }

  // Non-null by construction: `hasProAccessRole` returns false for null, so
  // reaching here means the predicate saw an object. The compiler cannot see
  // it — the predicate returns a plain boolean rather than a type guard, on
  // purpose, because narrowing to `{ role: UserRole }` would drop the other
  // fields callers read. (`tsconfig.json` sets `strictNullChecks: false`, so a
  // green tsc is not evidence about this either way.)
  return { outcome: "allowed", profile: profile as UserProfile };
}
